import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Button,
  Alert,
  LinearProgress,
} from '@mui/material';
import { DashboardLayout } from '../layout/DashboardLayout';
import { useDatabase } from '../../hooks/useDatabase';
import { useNavigate } from 'react-router-dom';
import {
  AccountTree as GraphIcon,
  Psychology as LearningIcon,
  MenuBook as KnowledgeIcon,
  ViewModule as BoardIcon,
  Science as BetaIcon,
  CloudUpload as UploadIcon,
  Assessment as ScoringIcon,
  AdminPanelSettings as AdminIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { CsvUpload } from './CsvUpload';
import { GmailSettings } from './GmailSettings';
import { AdminUserManagement } from './AdminUserManagement';
import { AccountManagerAssignment } from './AccountManagerAssignment';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { useAuth } from '../../contexts/AuthContext';
import { useUserProfile } from '../../hooks/useUserProfile';
import { 
  scoreAllInitiativesAgainstGoals, 
  InitiativeGoalScore
} from '../../utils/initiativeGoalScoring';

export function Settings() {
  const db = useDatabase();
  const navigate = useNavigate();
  const supabase = useSupabaseClient();
  const { user } = useAuth();
  const { profile: userProfile } = useUserProfile();
  const [loading, setLoading] = useState(true);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [connectionStats, setConnectionStats] = useState({
    contactToContact: 0,
    initiativeToInitiative: 0,
    contactToInitiative: 0,
    taskConnections: 0,
    total: 0
  });
  
  // Scoring state
  const [scoring, setScoring] = useState(false);
  const [scoringProgress, setScoringProgress] = useState(0);
  const [scoringResults, setScoringResults] = useState<InitiativeGoalScore[]>([]);
  const [scoringError, setScoringError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [contactsResponse, initiativesResponse, tasksResponse] = await Promise.all([
        db.getContacts(),
        db.getInitiatives(),
        db.getTasks()
      ]);

      const contactsData = contactsResponse.data || [];
      const initiativesData = initiativesResponse.data || [];
      const tasksData = tasksResponse.data || [];

      // Calculate connection counts using fresh data from API responses
      let contactToContact = 0;
      let initiativeToInitiative = 0;
      let contactToInitiative = 0;
      let taskConnections = 0;

      // Count contact-to-contact connections
      contactsData.forEach(contact => {
        contactToContact += contact.associated_contacts.length;
      });

      // Count initiative-to-initiative connections
      initiativesData.forEach(initiative => {
        initiativeToInitiative += initiative.related_initiatives.length;
      });

      // Count contact-to-initiative connections
      initiativesData.forEach(initiative => {
        contactToInitiative += initiative.contacts.length;
      });

      // Count task connections
      tasksData.forEach(task => {
        if (task.entity_id) {
          taskConnections += 1;
        }
      });

      // Divide by 2 since each connection is counted twice (bidirectional)
      contactToContact = Math.floor(contactToContact / 2);
      initiativeToInitiative = Math.floor(initiativeToInitiative / 2);

      setConnectionStats({
        contactToContact,
        initiativeToInitiative,
        contactToInitiative,
        taskConnections,
        total: contactToContact + initiativeToInitiative + contactToInitiative + taskConnections
      });
    } catch (error) {
      console.error('Failed to load connection data:', error);
    } finally {
      setLoading(false);
    }
  }, [db]);

  const handleUploadComplete = useCallback(async () => {
    // Refresh the connection statistics after upload
    loadData();
    
    // Trigger networking suggestions recalculation after CSV upload
    try {
      const { error: recalcError } = await db.recalculateNetworkingSuggestions();
      if (recalcError) {
        console.error('Error recalculating networking suggestions after CSV upload:', recalcError);
      } else {
        console.log('Networking suggestions recalculation triggered after CSV upload');
      }
    } catch (err) {
      console.error('Error triggering networking suggestions recalculation:', err);
    }
  }, [loadData, db]);

  const handleScoreInitiatives = useCallback(async () => {
    if (!user?.id) {
      setScoringError('User must be authenticated to score initiatives');
      return;
    }

    setScoring(true);
    setScoringProgress(0);
    setScoringError(null);
    setScoringResults([]);

    try {
      // Step 1: Load initiatives
      setScoringProgress(25);
      const { data: initiatives, error: initiativesError } = await db.getInitiatives();
      if (initiativesError) throw initiativesError;

      // Step 2: Load goals
      setScoringProgress(50);
      const { data: goals, error: goalsError } = await supabase
        .from('goals')
        .select('*')
        .eq('user_id', user.id);

      if (goalsError) throw goalsError;

      if (!initiatives || initiatives.length === 0) {
        setScoringError('No initiatives found to score');
        return;
      }

      if (!goals || goals.length === 0) {
        setScoringError('No goals found. Please add some goals first.');
        return;
      }

      // Step 3: Calculate scores
      setScoringProgress(75);
      const scores = await scoreAllInitiativesAgainstGoals(
        initiatives, 
        goals,
        (current, total) => {
          const progress = 75 + (current / total) * 15; // 75-90% for scoring
          setScoringProgress(Math.round(progress));
        }
      );

      // Step 4: Store scores in database
      setScoringProgress(90);
      
      const { error: storeError } = await db.storeInitiativeGoalScores(scores);
      if (storeError) {
        throw new Error(`Failed to store scores in database: ${storeError.message}`);
      }

      setScoringProgress(100);
      setScoringResults(scores);
      
      // Refresh the page data
      await loadData();
      
    } catch (error) {
      console.error('Error scoring initiatives:', error);
      setScoringError(error instanceof Error ? error.message : 'Failed to score initiatives');
    } finally {
      setScoring(false);
      setScoringProgress(0);
    }
  }, [user, db, supabase, loadData]);

  useEffect(() => {
    loadData();
  }, [loadData]);


  return (
    <DashboardLayout title="Settings">
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
            Settings
          </Typography>
          
          {/* CSV Upload Section */}
          <Box sx={{ mb: 4, pb: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <UploadIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
                Data Import
              </Typography>
            </Box>
            <CsvUpload onUploadComplete={handleUploadComplete} />
          </Box>

          {/* Gmail Integration Section */}
          <GmailSettings />

          {/* Admin User Management Section - Only visible to admins */}
          {userProfile?.user_type === 'admin' && (
            <>
              <Box sx={{ mb: 4, pb: 3, borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AdminIcon sx={{ color: 'primary.main' }} />
                    <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
                      User Management
                    </Typography>
                  </Box>
                  <Button
                    variant={showUserManagement ? 'outlined' : 'contained'}
                    startIcon={showUserManagement ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={() => setShowUserManagement(!showUserManagement)}
                    sx={{ minWidth: 180 }}
                  >
                    {showUserManagement ? 'Hide Users' : 'View All Users'}
                  </Button>
                </Box>
                {showUserManagement && <AdminUserManagement />}
              </Box>

              {/* Account Manager Assignment Section */}
              <Box sx={{ mb: 4, pb: 3, borderBottom: 1, borderColor: 'divider' }}>
                <AccountManagerAssignment />
              </Box>
            </>
          )}

          {/* Initiative-Goal Scoring Section */}
          <Box sx={{ mb: 4, pb: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <ScoringIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
                Initiative-Goal Scoring
              </Typography>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Score how well each initiative advances your goals. This analysis considers keyword matching, 
              description alignment, tag relevance, and other factors to provide scores out of 100.
            </Typography>

            {scoringError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {scoringError}
              </Alert>
            )}

            {scoring && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Scoring initiatives against goals... {scoringProgress}%
                </Typography>
                <LinearProgress variant="determinate" value={scoringProgress} />
              </Box>
            )}

            {scoringResults.length > 0 && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Successfully scored {scoringResults.length} initiative-goal combinations. 
                Scores are now available on initiative pages.
              </Alert>
            )}

            <Button
              variant="contained"
              onClick={handleScoreInitiatives}
              disabled={scoring}
              startIcon={<ScoringIcon />}
            >
              {scoring ? 'Scoring...' : 'Score Initiatives Against Goals'}
            </Button>
          </Box>

          {/* Connection Statistics Section */}
          <Box sx={{ mb: 4, pb: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              Connection Statistics
            </Typography>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="primary.main" sx={{ fontWeight: 600, mb: 1 }}>
                        {connectionStats.total}
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary">
                        Total Connections
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="primary.main" sx={{ fontWeight: 600, mb: 1 }}>
                        {connectionStats.contactToContact}
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary">
                        Contact ↔ Contact
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="primary.main" sx={{ fontWeight: 600, mb: 1 }}>
                        {connectionStats.initiativeToInitiative}
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary">
                        Initiative ↔ Initiative
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="primary.main" sx={{ fontWeight: 600, mb: 1 }}>
                        {connectionStats.contactToInitiative}
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary">
                        Contact ↔ Initiative
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="primary.main" sx={{ fontWeight: 600, mb: 1 }}>
                        {connectionStats.taskConnections}
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary">
                        Task Connections
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </Box>

          {/* Beta Features Section */}
          <Box sx={{ mb: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <BetaIcon sx={{ color: 'warning.main' }} />
              <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
                Beta Features
              </Typography>
              <Typography variant="body2" color="warning.main" sx={{ ml: 1 }}>
                (Experimental)
              </Typography>
            </Box>
            
            {/* Board */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        backgroundColor: 'warning.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <BoardIcon />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Board of Directors
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        AI-powered advisory board for strategic decision making
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<BoardIcon />}
                    onClick={() => navigate('/board')}
                    sx={{ minWidth: 140 }}
                  >
                    Open Board
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Knowledge Base */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        backgroundColor: 'primary.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <KnowledgeIcon />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Knowledge Base
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Access articles, documents, and knowledge resources
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<KnowledgeIcon />}
                    onClick={() => navigate('/knowledge')}
                    sx={{ minWidth: 140 }}
                  >
                    Open Knowledge
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Knowledge Graph */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        backgroundColor: 'secondary.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <GraphIcon />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Knowledge Graph
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Interactive visualization of concepts, documents, skills, and relationships
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<GraphIcon />}
                    onClick={() => navigate('/knowledge-graph')}
                    sx={{ minWidth: 140 }}
                  >
                    Open Graph
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Learning Platform */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        backgroundColor: 'success.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <LearningIcon />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Learning Platform
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Interactive mind maps, assessments, and personalized learning paths
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<LearningIcon />}
                    onClick={() => navigate('/learning')}
                    sx={{ minWidth: 140 }}
                  >
                    Start Learning
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>

        </Paper>
      </Container>
    </DashboardLayout>
  );
} 